import React, { useState, useEffect } from 'react'
import {
  Drawer,
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import VideoCall from '@mui/icons-material/VideoCall'
import Upload from '@mui/icons-material/Upload'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Send from '@mui/icons-material/Send'
import Assessment from '@mui/icons-material/Assessment'
import JoiningDetailsContent from './JoiningDetailsContent'
import UploadPrescriptionContent from './UploadPrescriptionContent'
import AddVitalsContent from './AddVitalsContent'
import SendForReferralContent from './SendForReferralContent'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'

// Styled components for modern look
const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backdropFilter: 'blur(10px)',
}))

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  minHeight: '56px',
  position: 'sticky',
  top: '65px',
  background: '#fff',
  zIndex: 1,
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '3px 3px 0 0',
    background: 'linear-gradient(90deg, #4338ca 0%, #6366f1 100%)',
  },
  '& .MuiTabs-flexContainer': {
    gap: '4px',
  },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minHeight: '56px',
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.6)',
  padding: '12px 16px',
  '&.Mui-selected': {
    color: '#4338ca',
    fontWeight: 600,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}))

const DrawerContent = styled(Box)(({ theme }) => ({
  padding: '20px',
  paddingBottom: '40px',
  overflowY: 'auto',
  overflowX: 'hidden',
  flex: 1,
  minHeight: 0,
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(67, 56, 202, 0.3)',
    borderRadius: '10px',
    '&:hover': {
      background: 'rgba(67, 56, 202, 0.5)',
    },
  },
}))

// Tab configurations
const TAB_CONFIGS = {
  joiningDetails: {
    id: 'joiningDetails',
    label: 'Joining Details',
    icon: <VideoCall />,
  },
  uploadPrescription: {
    id: 'uploadPrescription',
    label: 'Upload Rx',
    icon: <Upload />,
  },
  addVitals: {
    id: 'addVitals',
    label: 'Add Vitals',
    icon: <LocalHospital />,
  },
  referPatient: {
    id: 'referPatient',
    label: 'Refer Patient',
    icon: <Send />,
  },
}

/**
 * UnifiedActionsDrawer - A mobile-friendly drawer that shows all actions as tabs
 * 
 * @param {boolean} open - Whether the drawer is open
 * @param {function} onClose - Function to close the drawer
 * @param {string} initialTab - Initial tab to show
 * @param {array} availableTabs - Array of tab ids to show
 * @param {object} bookedSlot - The booked slot data
 * @param {object} mentorProfileData - Mentor profile data
 * @param {object} clinicLocationData - Clinic location data
 * @param {function} refreshBookedSlots - Function to refresh booked slots
 * @param {object} apiSignatures - Object containing all API signatures
 * @param {object} dialogSettings - Settings for dialogs
 */
export default function UnifiedActionsDrawer({
  open,
  onClose,
  initialTab = 'joiningDetails',
  availableTabs = ['joiningDetails', 'uploadPrescription', 'addVitals', 'referPatient'],
  bookedSlot,
  mentorProfileData,
  clinicLocationData,
  refreshBookedSlots,
  apiSignatures = {},
  dialogSettings = {},
  forMentor = false,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [currentTab, setCurrentTab] = useState(initialTab)
  
  // Lock body scroll when drawer is open
  useBodyScrollLock(open, 1300)

  // Reset tab when drawer opens
  useEffect(() => {
    if (open) {
      setCurrentTab(initialTab)
    }
  }, [open, initialTab])

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  const renderTabContent = () => {
    switch (currentTab) {
      case 'joiningDetails':
        return (
          <JoiningDetailsContent
            bookedEventInfo={bookedSlot}
            refreshBookedSlots={refreshBookedSlots}
            sendSecureSessionLinkToPatientSignature={
              apiSignatures.sendSecureSessionLinkToPatientSignature ||
              '/api/secure/cc/support/events/secure-join-link/send'
            }
            sendPublicSessionLinkToPatientSignature={
              apiSignatures.sendPublicSessionLinkToPatientSignature ||
              '/api/secure/cc/support/events/public-join-link/send'
            }
            showSendingLinkButtonsToSupport={dialogSettings.showSendingLinkButtonsToSupport}
          />
        )
      case 'uploadPrescription':
        return (
          <UploadPrescriptionContent
            bookedEventInfo={bookedSlot}
            mentorUserId={mentorProfileData?.userId}
            refreshBookedSlots={refreshBookedSlots}
            showPatientHistoryInUploadPrescriptionWidget={
              dialogSettings.showPatientHistoryInUploadPrescriptionWidget
            }
            patientHistoryGetApiSignature={apiSignatures.patientHistoryGetApiSignature}
            galleryImagesSearch={dialogSettings.galleryImagesSearch}
            showGallery={dialogSettings.showGalleryForPrescriptionImages}
            fileUploadApiSignature={apiSignatures.fileUploadApiSignature}
            prescriptionImagesGetApiSignature={
              apiSignatures.prescriptionImagesGetApiSignature ||
              '/api/secure/doctor/prescription/images/get-by-eventId'
            }
            prescriptionImagesSaveApiSignature={
              apiSignatures.prescriptionImagesSaveApiSignature ||
              '/api/secure/doctor/prescription/images/save'
            }
          />
        )
      case 'addVitals':
        return (
          <AddVitalsContent
            bookedSlot={bookedSlot}
            mentorProfileData={mentorProfileData}
            clinicLocationData={clinicLocationData}
            refreshBookedSlots={refreshBookedSlots}
            vitalsFormGetApiSignature={apiSignatures.vitalsFormGetApiSignature}
          />
        )
      case 'referPatient':
        return (
          <SendForReferralContent
            bookedEventInfo={bookedSlot}
            refreshBookedSlots={refreshBookedSlots}
            mentorProfileData={mentorProfileData}
            clinicLocationData={clinicLocationData}
            prescriptionGetApiSignature={
              apiSignatures.prescriptionGetApiSignature ||
              '/api/secure/doctor/prescription/get-by-eventId'
            }
            prescriptionSaveApiSignature={
              apiSignatures.prescriptionSaveApiSignature ||
              '/api/secure/doctor/prescription/save'
            }
            showGallery={dialogSettings.showGalleryForPrescriptionImages}
            galleryImagesSearch={dialogSettings.galleryImagesSearch}
            fileUploadApiSignature={apiSignatures.fileUploadApiSignature}
          />
        )
      default:
        return null
    }
  }

  const filteredTabs = availableTabs
    .map((tabId) => TAB_CONFIGS[tabId])
    .filter(Boolean)

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '600px',
          maxWidth: '100%',
          height: isMobile ? '92vh' : '100%',
          maxHeight: isMobile ? '92vh' : '100vh',
          borderRadius: isMobile ? '20px 20px 0 0' : 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      ModalProps={{
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
      }}
    >
      {/* Header */}
      <DrawerHeader>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#4338ca',
            fontSize: { xs: '18px', md: '20px' },
          }}
        >
          Quick Actions
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(0, 0, 0, 0.6)',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <Close />
        </IconButton>
      </DrawerHeader>

      {/* Tabs */}
      {filteredTabs.length > 1 && (
        <StyledTabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          {filteredTabs.map((tab) => (
            <StyledTab
              key={tab.id}
              value={tab.id}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </StyledTabs>
      )}

      {/* Tab Content */}
      <DrawerContent>{renderTabContent()}</DrawerContent>
    </Drawer>
  )
}

